"use strict";
/**
 * Access Level, Shelf State and Catalog State — the three orthogonal axes of content state.
 *
 * Spec `content-visibility-model` split one overloaded `visibility` field into independent axes,
 * because a single field carrying both "who may open this" and "does the owner still consider this
 * active" is what made **Make Private**, **Archive** and **Unpublish** write near-identical records.
 *
 * ## Access_Level — `Visibility`, for pattern / project / collection
 *
 *     public    in discovery, opens for anyone
 *     unlisted  out of discovery, opens for anyone holding the link
 *     private   out of discovery, refused to a third party
 *
 * `archived` is deliberately ABSENT from this enum. Archival is a Shelf_State, and an enum member
 * named `archived` is precisely how the two axes came to be conflated. Removing it is also the
 * migration mechanism for the rest of the spec: `VISIBILITY_TO_USAGE_VISIBILITY` in
 * `./usage-visibility.ts` is declared `Record<Visibility, UsageVisibility>`, so every site that
 * still reasons about an archived *visibility* fails to compile rather than drifting silently.
 *
 * The field on the record keeps the name `visibility` rather than being renamed to `access_level`:
 * once `archived` leaves the value set the name is unambiguous, and renaming would churn the
 * DynamoDB records, the API responses, the OpenSearch mappings and every frontend prop for no
 * behavioural gain. The spec constrains the TOKEN (`unlisted`), not the field name.
 *
 * ## Shelf_State — `ShelfState`, for pattern / project / collection / material
 *
 * Whether the owner still treats the item as active. Independent of Access_Level, so archiving
 * changes where an item is shelved without changing who can reach it.
 *
 * ## Catalog_State — `CatalogState`, for materials only
 *
 * A material has NO Access_Level: there is no link-sharing concept for a personal material, so
 * there is nothing for `unlisted` to mean. It carries instead a Catalog_State — `public` (offered
 * to the shared catalog) or `private` (personal only) — plus a Shelf_State. This replaced
 * `MaterialVisibility`, which was `Exclude<Visibility, PRIVATE>` and therefore yielded
 * `public | archived`: wrong on both counts, since a material CAN be private and CANNOT have an
 * archived access level.
 *
 * ## Terminology discipline (spec Requirement 3)
 *
 * Each token names exactly one axis and is not a synonym for any other:
 * `unlisted` / `private` are Access_Levels, `archived` is a Shelf_State, `unpublished` / `draft` /
 * `temp` are Lifecycle_States. A sentence describing two axes names both — "an unlisted archived
 * pattern" — rather than collapsing them into one word.
 *
 * Requirements: 1.1, 1.6, 1.7, 2.1, 3.1, 3.5, 3.6 (spec `content-visibility-model`).
 */
// Created: 2026-07-20
// Updated: 2026-09-03
Object.defineProperty(exports, "__esModule", { value: true });
exports.PrivacyControls = exports.CATALOG_STATES = exports.CATALOG_STATE = exports.SHELF_STATES = exports.SHELF_STATE = exports.Visibility = void 0;
/**
 * Access_Level for a pattern, project or collection: who may open the item.
 *
 * Exactly three members, each distinguishable from the others on more than one observable
 * dimension (discovery, third-party listings, reference surfaces, direct URL, comments, PDF
 * download) so that no two are functionally interchangeable.
 *
 * There is NO `ARCHIVED` member and no compatibility alias for one — spec Requirement 3.5 forbids a
 * translation layer for a superseded token, and the dev data set is reset rather than migrated.
 */
var Visibility;
(function (Visibility) {
    /** In every discovery surface; opens for anyone. */
    Visibility["PUBLIC"] = "public";
    /** Out of every discovery surface; opens for anyone holding the link. Not private. */
    Visibility["UNLISTED"] = "unlisted";
    /** Out of every discovery surface; refused to a third party by direct URL. */
    Visibility["PRIVATE"] = "private";
})(Visibility || (exports.Visibility = Visibility = {}));
/**
 * Shelf_State values. Applies to pattern, project, collection AND material.
 *
 * Declared as an `as const` object plus a derived union rather than an enum, matching
 * `USAGE_VISIBILITY` in `./usage-visibility.ts`: the runtime value set and the compile-time union
 * cannot disagree, and `SHELF_STATES` gives readers a single enumeration to iterate.
 */
exports.SHELF_STATE = {
    /** The owner still treats the item as current. The absence of an archive, not a separate state. */
    ACTIVE: 'active',
    /** The owner has retired the item. Access_Level is unaffected — existing links keep working. */
    ARCHIVED: 'archived',
};
/** Both Shelf_States, for readers that must enumerate them. Derived, so it cannot drift. */
exports.SHELF_STATES = Object.values(exports.SHELF_STATE);
/**
 * Catalog_State values — materials only.
 *
 * `public` means the material is offered to the shared catalog; `private` means personal only.
 * This is NOT an Access_Level and is NOT gated by the direct-URL access gate (spec Requirement
 * 1.7): it decides catalog membership, not who may read the row.
 */
exports.CATALOG_STATE = {
    /** Offered to the shared material catalog. */
    PUBLIC: 'public',
    /** Personal to the owner; not offered to the catalog. */
    PRIVATE: 'private',
};
/** Both Catalog_States, for readers that must enumerate them. Derived, so it cannot drift. */
exports.CATALOG_STATES = Object.values(exports.CATALOG_STATE);
/**
 * Privacy Controls Enum for Profile/Craft Room Settings
 * Controls what sections are visible on user profiles/craft rooms
 * NOTE: Does NOT bulk-update individual content visibility
 */
var PrivacyControls;
(function (PrivacyControls) {
    PrivacyControls["PUBLIC"] = "public";
    PrivacyControls["PRIVATE"] = "private";
})(PrivacyControls || (exports.PrivacyControls = PrivacyControls = {}));
